import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthModalService } from './auth-modal.service';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss']
})
export class AuthModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  protected authModalService = inject(AuthModalService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);

  signinForm!: FormGroup;
  signupForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  phoneForm!: FormGroup;
  otpForm!: FormGroup;
  
  authMethod: 'phone' | 'email' = 'phone';
  showOtpInput = false;
  
  showPassword = false;
  showConfirmPassword = false;
  isSubmitting = false;

  constructor() {
    this.authService.userProfile$.subscribe(user => {
      if (user && this.authModalService.isOpen()) {
        const redirectPath = this.authModalService.redirectUrl();
        this.authModalService.close();
        if (redirectPath) {
          this.router.navigateByUrl(redirectPath);
        }
      }
    });
  }

  ngOnInit() {
    this.initForms();
  }

  initForms() {
    this.signinForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });

    this.signupForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.phoneForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      name: ['']
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { passwordsMismatch: true };
  }

  closeModal() {
    this.authModalService.close();
    this.resetForms();
  }

  closeOnBackdrop(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.closeModal();
    }
  }

  resetForms() {
    this.signinForm.reset();
    this.signupForm.reset();
    this.forgotPasswordForm.reset();
    this.phoneForm.reset();
    this.otpForm.reset();
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.isSubmitting = false;
    this.showOtpInput = false;
  }

  switchToSignUp() {
    this.authModalService.openSignUp();
  }

  switchToSignIn() {
    this.authModalService.openSignIn();
  }

  switchToForgotPassword() {
    this.authModalService.openForgotPassword();
  }

  switchToPhone() {
    this.authMethod = 'phone';
    this.showOtpInput = false;
  }

  switchToEmail() {
    this.authMethod = 'email';
    this.showOtpInput = false;
  }

  async submitSignIn() {
    if (this.signinForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const { email, password } = this.signinForm.value;
      await this.authService.signin(email, password);
      this.toastService.success('Welcome back!');
      this.closeModal();
    } catch (error: any) {
      this.toastService.error(error.message || 'Sign in failed');
    } finally {
      this.isSubmitting = false;
    }
  }

  async submitSignUp() {
    if (this.signupForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const { name, email, password } = this.signupForm.value;
      await this.authService.signup(email, password, name);
      this.toastService.success('Account created successfully!');
      this.closeModal();
    } catch (error: any) {
      this.toastService.error(error.message || 'Sign up failed');
    } finally {
      this.isSubmitting = false;
    }
  }

  async submitForgotPassword() {
    if (this.forgotPasswordForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const { email } = this.forgotPasswordForm.value;
      await this.authService.resetPassword(email);
      this.toastService.success('Password reset email sent!');
      this.closeModal();
    } catch (error: any) {
      this.toastService.error(error.message || 'Failed to send reset email');
    } finally {
      this.isSubmitting = false;
    }
  }

  async loginWithGoogle() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.authService.googleSignin();
      this.toastService.success('Welcome!');
      this.closeModal();
    } catch (error: any) {
      this.toastService.error(error.message || 'Google sign in failed');
    } finally {
      this.isSubmitting = false;
    }
  }

  async submitPhone() {
    if (this.phoneForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const { phoneNumber } = this.phoneForm.value;
      const fullNumber = '+91' + phoneNumber; // Auto-prepend country code
      await this.authService.signInWithPhone(fullNumber, 'recaptcha-container');
      this.showOtpInput = true;
      this.toastService.success('OTP sent to your phone');
    } catch (error: any) {
      this.toastService.error(error.message || 'Failed to send OTP');
    } finally {
      this.isSubmitting = false;
    }
  }

  async submitOtp() {
    if (this.otpForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    const { otp } = this.otpForm.value;
    const name = this.phoneForm.get('name')?.value || null;
    try {
      await this.authService.verifyOtp(otp, name);
      this.toastService.success('Welcome!');
      this.closeModal();
    } catch (error: any) {
      this.toastService.error(error.message || 'Invalid OTP');
    } finally {
      this.isSubmitting = false;
    }
  }
}
